import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Input,
  Separator,
  Spinner,
  Table,
  Text,
  Textarea,
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { FiChevronDown, FiChevronRight, FiEdit2, FiGlobe, FiLock, FiPlus, FiTrash2 } from "react-icons/fi"

import { Surveys, type SurveyPublic, type SurveyWithQuestionsPublic } from "@/client"
import useAuth from "@/hooks/useAuth"
import { UserRole } from "@/types/permissions"

export const Route = createFileRoute("/_layout/provider/surveys")({
  component: SurveysPage,
})

// ── Option editor ─────────────────────────────────────────────────────────────

interface OptionEditorProps {
  surveyId: string
  questionId: string
  option: { id: string; title?: string | null; description?: string | null; score: number; sequence: number }
  readonly: boolean
  onDeleted: () => void
}

function OptionEditor({ surveyId, questionId, option, readonly, onDeleted }: OptionEditorProps) {
  const qc = useQueryClient()
  const [title, setTitle] = useState(option.title ?? "")
  const [score, setScore] = useState(String(option.score ?? 0))
  const [dirty, setDirty] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      Surveys.updateSurveyQuestionOption({
        path: { survey_question_option_id: option.id },
        body: { id: option.id, surveyId, questionId, score: option.score, sequence: option.sequence, title },
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["survey", surveyId] }); setDirty(false) },
  })

  const del = useMutation({
    mutationFn: () => Surveys.deleteSurveyQuestionOption({ path: { survey_question_option_id: option.id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["survey", surveyId] }); onDeleted() },
  })

  return (
    <Flex align="center" gap={2} py={1}>
      <Input
        size="xs"
        value={title}
        readOnly={readonly}
        onChange={(e) => { setTitle(e.target.value); setDirty(true) }}
        placeholder="Option label"
        flex={1}
      />
      <Input
        size="xs"
        value={score}
        readOnly={readonly}
        onChange={(e) => { setScore(e.target.value); setDirty(true) }}
        placeholder="Score"
        w="60px"
        type="number"
      />
      {!readonly && (
        <>
          {dirty && (
            <Button size="xs" variant="solid" colorPalette="blue" onClick={() => save.mutate()} loading={save.isPending}>
              Save
            </Button>
          )}
          <IconButton size="xs" variant="ghost" colorPalette="red" aria-label="Delete option" onClick={() => del.mutate()}>
            <FiTrash2 />
          </IconButton>
        </>
      )}
    </Flex>
  )
}

// ── Question editor ───────────────────────────────────────────────────────────

type SurveyQuestion = NonNullable<SurveyWithQuestionsPublic["questions"]>[number]

interface QuestionEditorProps {
  surveyId: string
  question: SurveyQuestion
  readonly: boolean
  onDeleted: () => void
}

function QuestionEditor({ surveyId, question, readonly, onDeleted }: QuestionEditorProps) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [text, setText] = useState(question.question ?? "")
  const [description, setDescription] = useState(question.description ?? "")
  const [dirty, setDirty] = useState(false)
  const [addingOption, setAddingOption] = useState(false)
  const [newOptionTitle, setNewOptionTitle] = useState("")
  const [newOptionScore, setNewOptionScore] = useState("0")

  const save = useMutation({
    mutationFn: () =>
      Surveys.updateSurveyQuestion({
        path: { survey_question_id: question.id },
        body: { id: question.id, surveyId, sequence: question.sequence, question: text, description },
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["survey", surveyId] }); setDirty(false) },
  })

  const del = useMutation({
    mutationFn: () => Surveys.deleteSurveyQuestion({ path: { survey_question_id: question.id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["survey", surveyId] }); onDeleted() },
  })

  const addOption = useMutation({
    mutationFn: () =>
      Surveys.createSurveyQuestionOption({
        path: { survey_question_id: question.id },
        body: {
          surveyId,
          questionId: question.id,
          title: newOptionTitle,
          description: "",
          score: Number(newOptionScore),
          sequence: (question.options ?? []).length + 1,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["survey", surveyId] })
      setNewOptionTitle("")
      setNewOptionScore("0")
      setAddingOption(false)
    },
  })

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden" mb={2}>
      <Flex
        px={3} py={2} bg="gray.50" align="center" gap={2} cursor="pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        <Text fontSize="sm" flex={1} fontWeight="medium">{question.question || "Untitled question"}</Text>
        <Badge colorPalette="gray" size="sm">{question.options?.length ?? 0} options</Badge>
        {!readonly && (
          <IconButton
            size="xs" variant="ghost" colorPalette="red" aria-label="Delete question"
            onClick={(e) => { e.stopPropagation(); del.mutate() }}
          >
            <FiTrash2 />
          </IconButton>
        )}
      </Flex>

      {expanded && (
        <Box px={3} py={3}>
          {/* Question text + description */}
          <Box mb={3}>
            <Flex gap={2} mb={2} align="flex-end">
              <Box flex={1}>
                <Text fontSize="xs" color="gray.500" mb={1}>Question text</Text>
                <Input
                  size="sm"
                  value={text}
                  readOnly={readonly}
                  onChange={(e) => { setText(e.target.value); setDirty(true) }}
                  placeholder="Enter question…"
                />
              </Box>
              {!readonly && dirty && (
                <Button size="sm" colorPalette="blue" onClick={() => save.mutate()} loading={save.isPending}>Save</Button>
              )}
            </Flex>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Description</Text>
              <Textarea
                size="sm"
                value={description}
                readOnly={readonly}
                onChange={(e) => { setDescription(e.target.value); setDirty(true) }}
                placeholder="Optional description or helper text…"
                rows={2}
              />
            </Box>
          </Box>

          {/* Options */}
          <Text fontSize="xs" color="gray.500" mb={1}>Answer options</Text>
          {(question.options ?? []).map((opt: NonNullable<SurveyQuestion["options"]>[number]) => (
            <OptionEditor
              key={opt.id}
              surveyId={surveyId}
              questionId={question.id}
              option={opt}
              readonly={readonly}
              onDeleted={() => {}}
            />
          ))}

          {!readonly && (
            addingOption ? (
              <Flex gap={2} mt={2} align="center">
                <Input size="xs" placeholder="Option label" value={newOptionTitle}
                  onChange={(e) => setNewOptionTitle(e.target.value)} flex={1} />
                <Input size="xs" placeholder="Score" value={newOptionScore} type="number" w="60px"
                  onChange={(e) => setNewOptionScore(e.target.value)} />
                <Button size="xs" colorPalette="blue" onClick={() => addOption.mutate()}
                  loading={addOption.isPending} disabled={!newOptionTitle}>Add</Button>
                <Button size="xs" variant="ghost" onClick={() => setAddingOption(false)}>Cancel</Button>
              </Flex>
            ) : (
              <Button size="xs" variant="ghost" mt={2} onClick={() => setAddingOption(true)}>
                <FiPlus /> Add option
              </Button>
            )
          )}
        </Box>
      )}
    </Box>
  )
}

// ── Survey editor panel ───────────────────────────────────────────────────────

interface SurveyEditorProps {
  survey: SurveyPublic
  readonly: boolean
  onClose: () => void
}

function SurveyEditor({ survey, readonly, onClose }: SurveyEditorProps) {
  const qc = useQueryClient()
  const [title, setTitle] = useState(survey.title ?? "")
  const [description, setDescription] = useState(survey.description ?? "")
  const [dirty, setDirty] = useState(false)
  const [addingQuestion, setAddingQuestion] = useState(false)
  const [newQText, setNewQText] = useState("")

  const { data, isLoading } = useQuery({
    queryKey: ["survey", survey.id],
    queryFn: () => Surveys.readSurvey({ path: { survey_id: survey.id } }),
  })

  const saveMeta = useMutation({
    mutationFn: () =>
      Surveys.updateSurvey({
        path: { survey_id: survey.id },
        body: { id: survey.id },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["surveys"] })
      qc.invalidateQueries({ queryKey: ["survey", survey.id] })
      setDirty(false)
    },
  })

  const addQuestion = useMutation({
    mutationFn: () =>
      Surveys.createSurveyQuestion({
        path: { survey_id: survey.id },
        body: {
          surveyId: survey.id,
          question: newQText,
          description: "",
          sequence: (data?.questions?.length ?? 0) + 1,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["survey", survey.id] })
      setNewQText("")
      setAddingQuestion(false)
    },
  })

  return (
    <Box borderWidth="1px" borderRadius="lg" p={5} bg="white">
      <Flex align="center" justify="space-between" mb={4}>
        <Flex align="center" gap={2}>
          <Heading size="sm">{survey.title || "Untitled Survey"}</Heading>
          {survey.isGlobal && (
            <Badge colorPalette="purple" size="sm" display="flex" alignItems="center" gap={1}>
              <FiGlobe size={10} /> Global
            </Badge>
          )}
          {readonly && (
            <Badge colorPalette="gray" size="sm" display="flex" alignItems="center" gap={1}>
              <FiLock size={10} /> View only
            </Badge>
          )}
        </Flex>
        <Button size="sm" variant="ghost" colorPalette="gray" onClick={onClose}>Close</Button>
      </Flex>

      {/* Title + description */}
      <Grid templateColumns="1fr 1fr" gap={4} mb={4}>
        <GridItem>
          <Text fontSize="xs" color="gray.500" mb={1}>Title</Text>
          <Input
            value={title}
            readOnly={readonly}
            onChange={(e) => { setTitle(e.target.value); setDirty(true) }}
            placeholder="Survey title"
          />
        </GridItem>
        <GridItem>
          <Text fontSize="xs" color="gray.500" mb={1}>Description</Text>
          <Textarea
            value={description}
            readOnly={readonly}
            onChange={(e) => { setDescription(e.target.value); setDirty(true) }}
            placeholder="Survey description"
            rows={2}
          />
        </GridItem>
      </Grid>

      {!readonly && dirty && (
        <Flex mb={4}>
          <Button size="sm" colorPalette="blue" onClick={() => saveMeta.mutate()} loading={saveMeta.isPending}>
            Save changes
          </Button>
        </Flex>
      )}

      <Separator mb={4} />

      <Flex align="center" justify="space-between" mb={3}>
        <Text fontWeight="semibold" fontSize="sm">Questions ({data?.questions?.length ?? 0})</Text>
        {!readonly && (
          <Button size="sm" variant="outline" onClick={() => setAddingQuestion(true)}>
            <FiPlus /> Add question
          </Button>
        )}
      </Flex>

      {isLoading && <Spinner size="sm" />}

      {data?.questions?.map((q) => (
        <QuestionEditor key={q.id} surveyId={survey.id} question={q} readonly={readonly} onDeleted={() => {}} />
      ))}

      {!readonly && addingQuestion && (
        <Flex gap={2} mt={2} align="center">
          <Input placeholder="Question text…" value={newQText} onChange={(e) => setNewQText(e.target.value)} size="sm" flex={1} />
          <Button size="sm" colorPalette="blue" onClick={() => addQuestion.mutate()}
            loading={addQuestion.isPending} disabled={!newQText}>Add</Button>
          <Button size="sm" variant="ghost" onClick={() => setAddingQuestion(false)}>Cancel</Button>
        </Flex>
      )}
    </Box>
  )
}

// ── New survey form ───────────────────────────────────────────────────────────

interface NewSurveyFormProps {
  providerId: string
  isSuperuser: boolean
  onCreated: () => void
  onCancel: () => void
}

function NewSurveyForm({ providerId, isSuperuser, onCreated, onCancel }: NewSurveyFormProps) {
  const qc = useQueryClient()
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [isGlobal, setIsGlobal] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      Surveys.createSurvey({
        body: { title, description, isGlobal, providerId: isGlobal ? null : providerId },
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["surveys"] }); onCreated() },
  })

  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} mb={4} bg="blue.50" borderColor="blue.200">
      <Heading size="sm" mb={3}>New Survey</Heading>
      <Grid templateColumns="1fr 1fr" gap={3} mb={3}>
        <GridItem>
          <Text fontSize="xs" color="gray.500" mb={1}>Title</Text>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Survey title" size="sm" />
        </GridItem>
        <GridItem>
          <Text fontSize="xs" color="gray.500" mb={1}>Description</Text>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" size="sm" />
        </GridItem>
      </Grid>
      {isSuperuser && (
        <Flex align="center" gap={2} mb={3}>
          <input type="checkbox" id="isGlobal" checked={isGlobal} onChange={(e) => setIsGlobal(e.target.checked)} />
          <label htmlFor="isGlobal" style={{ fontSize: "0.875rem" }}>Global survey (available to all providers, editable by superusers only)</label>
        </Flex>
      )}
      <Flex gap={2}>
        <Button size="sm" colorPalette="blue" onClick={() => create.mutate()} loading={create.isPending} disabled={!title}>
          Create
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </Flex>
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function SurveysPage() {
  const { userRole } = useAuth()
  const isSuperuser = userRole === UserRole.SUPER_USER
  const isAdmin = isSuperuser || userRole === UserRole.PROVIDER_ADMIN || userRole === UserRole.PROVIDER_GROUP_ADMIN

  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""

  const { data, isLoading } = useQuery({
    queryKey: ["surveys", providerId],
    queryFn: () => Surveys.readSurveys({ query: { providerId: providerId || undefined } }),
  })

  const qc = useQueryClient()
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyPublic | null>(null)
  const [creating, setCreating] = useState(false)

  const del = useMutation({
    mutationFn: (id: string) => Surveys.deleteSurvey({ path: { survey_id: id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["surveys"] })
      if (selectedSurvey) setSelectedSurvey(null)
    },
  })

  const surveys = data?.data ?? []
  const globalSurveys = surveys.filter((s) => s.isGlobal)
  const providerSurveys = surveys.filter((s) => !s.isGlobal)

  const canEdit = (s: SurveyPublic) => {
    if (s.isGlobal) return isSuperuser
    return isAdmin
  }

  const renderRow = (s: SurveyPublic) => (
    <Table.Row key={s.id} bg={selectedSurvey?.id === s.id ? "blue.50" : undefined}>
      <Table.Cell>
        <Flex align="center" gap={2}>
          <Text fontSize="sm" fontWeight="medium">{s.title || "—"}</Text>
          {s.isGlobal && (
            <Badge colorPalette="purple" size="sm"><FiGlobe size={10} /> Global</Badge>
          )}
        </Flex>
        {s.description && <Text fontSize="xs" color="gray.400" truncate maxW="300px">{s.description}</Text>}
      </Table.Cell>
      <Table.Cell textAlign="right">
        <Flex justify="flex-end" gap={1}>
          <IconButton
            size="xs" variant="ghost" aria-label="Edit / View"
            colorPalette={canEdit(s) ? "blue" : "gray"}
            onClick={() => setSelectedSurvey(s)}
          >
            {canEdit(s) ? <FiEdit2 /> : <FiLock />}
          </IconButton>
          {canEdit(s) && (
            <IconButton
              size="xs" variant="ghost" colorPalette="red" aria-label="Delete"
              onClick={() => del.mutate(s.id)}
              loading={del.isPending}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Table.Cell>
    </Table.Row>
  )

  return (
    <Container maxW="full">
      <Flex pt={12} mb={6} align="center" justify="space-between">
        <Box>
          <Heading size="lg">Surveys</Heading>
          <Text color="gray.500" fontSize="sm">Manage survey templates for client assessments.</Text>
        </Box>
        {isAdmin && !creating && (
          <Button onClick={() => setCreating(true)}>
            <FiPlus /> New Survey
          </Button>
        )}
      </Flex>

      {creating && (
        <NewSurveyForm
          providerId={providerId}
          isSuperuser={isSuperuser}
          onCreated={() => setCreating(false)}
          onCancel={() => setCreating(false)}
        />
      )}

      <Grid templateColumns={selectedSurvey ? "1fr 1.5fr" : "1fr"} gap={6}>
        <GridItem>
          {isLoading && <Spinner />}

          {globalSurveys.length > 0 && (
            <Box mb={6}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                Global Surveys
              </Text>
              <Table.Root size="sm" variant="outline">
                <Table.Body>{globalSurveys.map(renderRow)}</Table.Body>
              </Table.Root>
            </Box>
          )}

          {providerSurveys.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                Provider Surveys
              </Text>
              <Table.Root size="sm" variant="outline">
                <Table.Body>{providerSurveys.map(renderRow)}</Table.Body>
              </Table.Root>
            </Box>
          )}

          {!isLoading && surveys.length === 0 && (
            <Text color="gray.400" fontSize="sm">No surveys found.</Text>
          )}
        </GridItem>

        {selectedSurvey && (
          <GridItem>
            <SurveyEditor
              survey={selectedSurvey}
              readonly={!canEdit(selectedSurvey)}
              onClose={() => setSelectedSurvey(null)}
            />
          </GridItem>
        )}
      </Grid>
    </Container>
  )
}
